/**
 * HORMONAL RESOLVER (MEV5)
 * Mechanism: Insulin Secretion & Incretin Surge
 * Standards: Bergman (1989), Vilsbøll et al. (2003)
 */

import { V5PhysiologicalState as PhysiologicalState } from '../types';

export const HormonalResolver = {
    
    /**
     * Calculates the total insulin secreted in the current minute.
     * @param state Current physiological state
     * @param totalGastricEmptying Grams of food leaving the stomach this minute
     * @param bolusInput Immediate FII load from a meal currently being ingested
     * @param profile User profile for sensitivity scaling
     * @param viscosityMod Blunting factor for the incretin surge (Phase 3.3)
     */
    compute(
        state: PhysiologicalState, 
        totalGastricEmptying: number, 
        bolusInput: number,
        profile: any,
        viscosityMod: number = 1.0
    ): number {
        // A. Profile Sensitivity Scaling
        // Low sensitivity = 0.7. So (1 / 0.7) = 1.42x more insulin required.
        const sensitivityFactor = profile?.insulin_sensitivity === 'LOW' ? 0.7 : (profile?.insulin_sensitivity === 'HIGH' ? 1.3 : 1.0);
        const hyperInsulinemiaMod = 1 / sensitivityFactor;

        // 1. REACTIVE SECRETION (Feedback Loop)
        // Pancreas reacts to circulating nutrients.
        const currentGlucose = state.fractions.glucose.blood + state.fractions.complex_carbs.blood + state.fractions.fructose.blood;
        const currentAminos = state.fractions.intact_protein.blood + state.fractions.free_amino_acids.blood;
        
        // Coefficients derived from V5.0 research, scaled by resistance
        const reactiveTerm = ((currentGlucose * 0.15) + (currentAminos * 0.05)) * hyperInsulinemiaMod;

        // 2. INCRETIN SURGE (Anticipatory Phase)
        // Pancreas reacts to food physical exit rate (GLP-1).
        // Weighted Viscosity (Phase 3.3) blunts this surge.
        const incretinTerm = (totalGastricEmptying * 0.2 * viscosityMod) * hyperInsulinemiaMod;

        // 3. IMMEDIATE BOLUS (First Phase Insulin)
        // High-speed response to ingestion signal.
        const bolusTerm = (bolusInput * 0.8) * hyperInsulinemiaMod;

        return reactiveTerm + incretinTerm + bolusTerm;
    }
};
